import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';
import { AppColorTokens } from '../../styles/colorTokens';

interface Props {
  computedAt?: string | Date | null;
  isExchangeOpen?: boolean;
  feedSource?: string;
  showLabel?: boolean;
  size?: 'small' | 'medium';
}

function getFreshnessState(
  computedAt: string | Date | null | undefined,
  isExchangeOpen: boolean,
  C: AppColorTokens
) {
  if (!isExchangeOpen) {
    return { label: 'CLOSED', color: C.dim, ageMinutes: -1 };
  }
  if (!computedAt) {
    return { label: 'No data', color: C.bear, ageMinutes: -1 };
  }
  const ageMinutes = Math.floor((Date.now() - new Date(computedAt).getTime()) / 60000);
  if (ageMinutes < 2)  return { label: 'Live',          color: C.bull,    ageMinutes };
  if (ageMinutes < 10) return { label: `${ageMinutes}m ago`, color: C.neutral, ageMinutes };
  if (ageMinutes < 20) return { label: `${ageMinutes}m ago`, color: C.warn,    ageMinutes };
  return                      { label: `${ageMinutes}m ago`, color: C.bear,    ageMinutes };
}

const FreshnessBadge: React.FC<Props> = ({
  computedAt, isExchangeOpen = true, feedSource, showLabel = true, size = 'small'
}) => {
  const { C } = useAppTheme();
  const { label, color, ageMinutes } = getFreshnessState(computedAt, isExchangeOpen, C);
  const isFallback = feedSource === 'ZERODHA_FALLBACK';
  const displayColor = isFallback ? C.warn : color;

  const tooltipText = !isExchangeOpen
    ? 'Exchange closed — no live data expected'
    : ageMinutes > 20
      ? `Data is ${ageMinutes} min old. Execution requires fresh data.`
      : ageMinutes > 10
        ? `Data is ${ageMinutes} min old. Check before executing.`
        : isFallback
          ? 'Using Zerodha fallback — primary feed failed'
          : `Data is fresh (${ageMinutes < 2 ? 'live' : ageMinutes + ' min ago'})`;

  const dotSize = size === 'small' ? 6 : 8;

  return (
    <Tooltip title={tooltipText} arrow>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{
          width: dotSize, height: dotSize, borderRadius: '50%',
          bgcolor: displayColor,
        }} />
        {showLabel && (
          <Typography variant="caption" sx={{
            color: displayColor,
            fontWeight: 700,
            fontSize: size === 'small' ? '0.6rem' : '0.7rem',
            letterSpacing: 0.3
          }}>
            {isFallback ? 'Fallback' : label}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default FreshnessBadge;
